import { NgModule, inject } from '@angular/core';
import { Routes, RouterModule, ActivatedRouteSnapshot, Router } from '@angular/router';
import { DomainValuesModule } from './pages/domain-values/domain-values.module';
import { DynamicModule } from './pages/dynamic/dynamic.module';
import { MediasModule } from './pages/medias/medias.module';
import { MessagesModule } from './pages/messages/messages.module';
import { NewsListModule } from './pages/news-list/news-list.module';
import { PermissionsModule } from './pages/permissions/permissions.module';
import { ScheduleModule } from './pages/schedule/schedule.module';
import { UsersModule } from './pages/users/users.module';
import { WelcomeModule } from './pages/welcome/welcome.module';
import { TemplatesModule } from './templates/templates.module';
import { OfflineModule } from './pages/offline/offline.module';
import { ProgramsModule } from './pages/programs/programs.module';
import { HistoryModule } from './pages/history/history.module';
import { ReportsListModule } from './pages/reports-list/reports-list.module';
import { ContactsModule } from './pages/contacts/contacts.module';
import { MapsModule } from './pages/maps/maps.module';
import { PropertiesModule } from './pages/properties/properties.module';
import { AccountRequestModule } from './pages/account-requests/account-requests.module';
import { Observable, from, map, of } from 'rxjs';
import { Permissions, PermissionsService } from './services/permissions.service';
import { LoggingService } from './services/logging.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BusinessIntelligencesModule } from './pages/business-intelligences/business-intelligences.module';
import { AuthenticatorService } from '@aws-amplify/ui-angular';

const routes: Routes = [
  { path: '', pathMatch: 'prefix', redirectTo: 'welcome' },
  {
    path: 'welcome',
    loadChildren: (): Promise<typeof WelcomeModule> =>
      import('./pages/welcome/welcome.module').then((m) => m.WelcomeModule),
  },
  {
    path: 'offline',
    loadChildren: (): Promise<typeof OfflineModule> =>
      import('./pages/offline/offline.module').then((m) => m.OfflineModule),
  },
  {
    path: 'schedule',
    loadChildren: (): Promise<typeof ScheduleModule> =>
      import('./pages/schedule/schedule.module').then((m) => m.ScheduleModule),
  },
  {
    path: 'messages',
    loadChildren: (): Promise<typeof MessagesModule> =>
      import('./pages/messages/messages.module').then((m) => m.MessagesModule),
  },
  {
    path: 'news-list',
    loadChildren: (): Promise<typeof NewsListModule> =>
      import('./pages/news-list/news-list.module').then((m) => m.NewsListModule),
  },
  {
    path: 'users',
    loadChildren: (): Promise<typeof UsersModule> => import('./pages/users/users.module').then((m) => m.UsersModule),
  },
  {
    path: 'permissions',
    loadChildren: (): Promise<typeof PermissionsModule> =>
      import('./pages/permissions/permissions.module').then((m) => m.PermissionsModule),
  },
  {
    path: 'domain-values',
    loadChildren: (): Promise<typeof DomainValuesModule> =>
      import('./pages/domain-values/domain-values.module').then((m) => m.DomainValuesModule),
  },
  {
    path: 'templates',
    loadChildren: (): Promise<typeof TemplatesModule> =>
      import('./templates/templates.module').then((m) => m.TemplatesModule),
  },
  {
    path: 'dynamic',
    loadChildren: (): Promise<typeof DynamicModule> =>
      import('./pages/dynamic/dynamic.module').then((m) => m.DynamicModule),
  },
  {
    path: 'maps',
    loadChildren: (): Promise<typeof MapsModule> => import('./pages/maps/maps.module').then((m) => m.MapsModule),
  },
  {
    path: 'investment-projects',
    loadChildren: (): Promise<typeof MapsModule> =>
      import('./pages/investment-projects/investment-projects.module').then((m) => m.InvestmentProjectsModule),
  },
  {
    path: 'project-notifications',
    loadChildren: () =>
      import('./pages/project-notifications/project-notifications.routes').then((c) => c.PROJECT_NOTIFICATIONS_ROUTES),
  },
  {
    path: 'medias',
    loadChildren: (): Promise<typeof MediasModule> =>
      import('./pages/medias/medias.module').then((m) => m.MediasModule),
  },
  {
    path: 'programs',
    loadChildren: (): Promise<typeof ProgramsModule> =>
      import('./pages/programs/programs.module').then((m) => m.ProgramsModule),
  },
  {
    path: 'properties',
    loadChildren: (): Promise<typeof PropertiesModule> =>
      import('./pages/properties/properties.module').then((m) => m.PropertiesModule),
  },
  {
    path: 'history',
    loadChildren: (): Promise<typeof HistoryModule> =>
      import('./pages/history/history.module').then((m) => m.HistoryModule),
  },
  {
    path: 'reports-list',
    loadChildren: (): Promise<typeof ReportsListModule> =>
      import('./pages/reports-list/reports-list.module').then((m) => m.ReportsListModule),
  },
  {
    path: 'contacts',
    loadChildren: (): Promise<typeof ContactsModule> =>
      import('./pages/contacts/contacts.module').then((m) => m.ContactsModule),
  },
  {
    path: 'invoices',
    loadChildren: (): Promise<typeof ContactsModule> =>
      import('./pages/invoices/invoices.module').then((m) => m.InvoicesModule),
  },
  {
    path: 'account-requests',
    loadChildren: (): Promise<typeof AccountRequestModule> =>
      import('./pages/account-requests/account-requests.module').then((m) => m.AccountRequestModule),
  },
  {
    path: 'business-intelligences',
    loadChildren: (): Promise<typeof BusinessIntelligencesModule> =>
      import('./pages/business-intelligences/business-intelligences.module').then((m) => m.BusinessIntelligencesModule),
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./pages/dashboard/dashboard.routes').then((r) => r.DASHBOARD_ROUTES),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'top' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}

export function canActivate(route: ActivatedRouteSnapshot, entity: string): Observable<boolean> {
  const permissionsService = inject(PermissionsService);
  const logger = inject(LoggingService);
  const router = inject(Router);
  const authenticatorService = inject(AuthenticatorService);

  if (authenticatorService.authStatus !== 'authenticated') {
    return of(true);
  }

  return from(permissionsService.userCan(Permissions.Read, entity)).pipe(
    map((userCan) => {
      if (!userCan) {
        logger.error(new Error('Not authorized to view this page: ' + route.url), false);
        router.navigate(['/summary']);
      }

      return userCan;
    }),
  );
}

export interface CanComponentDeactivate {
  canDeactivate: () => boolean | Observable<boolean>;
}

export function canDeactivate(component: CanComponentDeactivate): Observable<boolean> {
  const modal = inject(NzModalService);

  if (component?.canDeactivate()) {
    return of(true);
  }

  const title = $localize`WARNING!`;
  const message = $localize`You have unsaved changes. Press "Continue editing" to stay on this page or "Quit the page" to exit the page and lose your changes.`;

  const confirmModal = modal.confirm({
    nzTitle: title,
    nzContent: message,
    nzOkText: $localize`Quit the page`,
    nzCancelText: $localize`Continue editing`,
    nzOnOk: () => true,
  });

  return confirmModal.afterClose.pipe(
    map((result) => {
      return result === true;
    }),
  );
}
